/**
 * Pause overlay: resume, restart the level, quit to the map.
 *
 * Keyboard-first (arrows plus Enter, Esc resumes) with mouse as a bonus, so it
 * already satisfies the keyboard-only navigation pass in Prompt 12.
 */
import Phaser from 'phaser';
import { pressButton, styleButton } from '../ui/menuButton';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../../types';

export const PAUSE_DEPTH = 200;

export interface PauseMenuHandlers {
  onResume(): void;
  onRestart(): void;
  onQuit(): void;
}

interface MenuItem {
  readonly label: string;
  readonly action: () => void;
}

export class PauseMenu {
  private readonly container: Phaser.GameObjects.Container;
  private readonly items: MenuItem[];
  private readonly labels: Phaser.GameObjects.Text[] = [];
  private selected = 0;
  private open = false;

  constructor(scene: Phaser.Scene, handlers: PauseMenuHandlers) {
    this.items = [
      { label: 'Keep climbing', action: handlers.onResume },
      { label: 'Start this level over', action: handlers.onRestart },
      { label: 'Back to the map', action: handlers.onQuit },
    ];

    const dim = scene.add.rectangle(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0x0b1220,
      0.78,
    );

    const panel = scene.add.graphics();
    panel.fillStyle(0x16233a, 0.98);
    panel.fillRoundedRect(GAME_WIDTH / 2 - 320, 170, 640, 380, 28);
    panel.lineStyle(6, 0x8ecae6, 1);
    panel.strokeRoundedRect(GAME_WIDTH / 2 - 320, 170, 640, 380, 28);

    const title = scene.add
      .text(GAME_WIDTH / 2, 230, 'Paused', {
        fontFamily: FONT_FAMILY,
        fontSize: '56px',
        color: PALETTE.snow,
      })
      .setOrigin(0.5);

    this.container = scene.add
      .container(0, 0, [dim, panel, title])
      .setDepth(PAUSE_DEPTH)
      .setScrollFactor(0)
      .setVisible(false);

    this.items.forEach((item, index) => {
      const label = scene.add
        .text(GAME_WIDTH / 2, 330 + index * 64, item.label, {
          fontFamily: FONT_FAMILY,
          fontSize: '30px',
          color: PALETTE.snow,
        })
        .setOrigin(0.5)
        // Pinned like its container: Phaser hit-tests a container's children
        // by their own scroll factor, so without this the buttons stopped
        // answering clicks once the camera had climbed (keys still worked).
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });

      label.on('pointerover', () => this.select(index));
      label.on('pointerup', () => pressButton(label, item.action));

      this.labels.push(label);
      this.container.add(label);
    });

    const hint = scene.add
      .text(
        GAME_WIDTH / 2,
        520,
        IS_PORTRAIT ? 'Tap an option' : 'Arrow keys and Enter, or Esc to keep going',
        {
          fontFamily: FONT_FAMILY,
          fontSize: '18px',
          color: PALETTE.ice,
        },
      )
      .setOrigin(0.5);
    this.container.add(hint);

    this.select(0);
  }

  get isOpen(): boolean {
    return this.open;
  }

  show(): void {
    this.open = true;
    this.select(0);
    this.container.setVisible(true);
  }

  hide(): void {
    this.open = false;
    this.container.setVisible(false);
  }

  /** Called from the scene's keydown handling while the menu is open. */
  handleKey(key: string): void {
    if (!this.open) return;
    if (key === 'ArrowUp') this.select((this.selected + this.items.length - 1) % this.items.length);
    else if (key === 'ArrowDown') this.select((this.selected + 1) % this.items.length);
    else if (key === 'Enter' || key === ' ') {
      const label = this.labels[this.selected];
      const item = this.items[this.selected];
      if (label !== undefined && item !== undefined) pressButton(label, item.action);
    }
  }

  destroy(): void {
    this.container.destroy();
  }

  private select(index: number): void {
    this.selected = index;
    this.labels.forEach((label, i) => styleButton(label, i === index));
  }
}
